/*
 * Copyright (C) 2026 Bohemian Miser <https://substack.com/@bohemianmiser>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

'use client';

import Link from 'next/link';
import { ChefHat, Calendar, GitGraph, Copy, Star, ThumbsUp, ThumbsDown, Trash2, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { toggleStarAction, voteRecipeAction, copyRecipeAction, deleteRecipeAction, vetRecipeAction } from '@/app/actions';
import { useRouter } from 'next/navigation';
import { CompareToggle } from '@/components/recipe-comparison/compare-toggle';
import { useRecipeComparison } from '@/components/recipe-comparison/comparison-context';

interface RecipeCardProps {
  recipe: {
    id: string;
    title: string;
    previewIcon?: string;
    createdAt?: string;
    nodeCount: number;
    likes: number;
    dislikes: number;
    ownerId?: string;
    ownerName?: string;
    isVetted?: boolean;
  };
  userId?: string; // Optional: Current user ID for optimistic updates
  isAdmin?: boolean;
}

export function RecipeCard({ recipe, userId, isAdmin }: RecipeCardProps) {
  const router = useRouter();
  const [likes, setLikes] = useState(recipe.likes);
  const [isStarred, setIsStarred] = useState(false); // Optimistic default
  const [isCopying, setIsCopying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isVetting, setIsVetting] = useState(false);
  const [isVetted, setIsVetted] = useState(recipe.isVetted);
  // Gallery compare table (signed-in only; null when no provider is mounted).
  const isCompared = useRecipeComparison()?.isSelected(recipe.id) ?? false;

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this recipe?')) return;
    
    setIsDeleting(true);
    const res = await deleteRecipeAction(recipe.id);
    if (res.success) {
        setIsDeleted(true);
    } else {
        alert(res.error || 'Failed to delete');
        setIsDeleting(false);
    }
  };

  const handleVet = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsVetting(true);
      const res = await vetRecipeAction(recipe.id, true);
      if (res.success) {
          setIsVetted(true);
          // Optional: Refresh page to update list if in "Unvetted" view
          router.refresh();
      } else {
          alert(res.error || 'Failed to vet');
      }
      setIsVetting(false);
  };

  if (isDeleted) return null;

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsCopying(true);
    const result = await copyRecipeAction(recipe.id);
    if (result.newId) {
      router.push(`/lanes?id=${result.newId}`);
    } else {
      alert(result.error || 'Failed to copy');
      setIsCopying(false);
    }
  };

  const handleVote = async (e: React.MouseEvent, type: 'like' | 'dislike') => {
    e.preventDefault();
    e.stopPropagation();
    // Optimistic
    if (type === 'like') setLikes(prev => prev + 1);
    await voteRecipeAction(recipe.id, type);
  };

  const handleStar = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsStarred(prev => !prev);
    await toggleStarAction(recipe.id);
  };

  return (
    <Link href={`/lanes?id=${recipe.id}`} className="block group h-full">
      <div
        data-testid="recipe-card"
        data-compared={isCompared ? 'true' : undefined}
        className={`bg-zinc-900 border ${isCompared ? 'border-yellow-500 ring-1 ring-yellow-500/40' : !isVetted && isAdmin ? 'border-orange-500/50' : 'border-zinc-800'} rounded-xl overflow-hidden hover:border-yellow-500/50 hover:shadow-xl hover:shadow-yellow-500/5 transition-all duration-300 h-full flex flex-col relative`}
      >

        {/* Compare tick (top-left). Renders nothing for signed-out visitors. */}
        <CompareToggle recipeId={recipe.id} title={recipe.title} />

        {/* Actions Overlay (Visible on Hover for Desktop, Always for Touch) */}
        <div className="absolute top-2 right-2 z-10 flex gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            {isAdmin && !isVetted && (
                <button 
                    onClick={handleVet}
                    disabled={isVetting}
                    className="p-2 bg-black/50 hover:bg-green-500/20 text-zinc-300 hover:text-green-500 rounded-full backdrop-blur-sm transition-colors"
                    title="Approve (Vet) Recipe"
                >
                    <CheckCircle className={`w-4 h-4 ${isVetting ? 'animate-spin' : ''}`} />
                </button>
            )}
            <button 
                onClick={handleCopy} 
                disabled={isCopying}
                className="p-2 bg-black/50 hover:bg-yellow-500/20 text-zinc-300 hover:text-yellow-500 rounded-full backdrop-blur-sm transition-colors"
                title="Copy Recipe"
            >
                <Copy className="w-4 h-4" />
            </button>
            <button 
                onClick={handleStar}
                className={`p-2 bg-black/50 hover:bg-yellow-500/20 rounded-full backdrop-blur-sm transition-colors ${isStarred ? 'text-yellow-500' : 'text-zinc-300'}`}
                title="Star Recipe"
            >
                <Star className={`w-4 h-4 ${isStarred ? 'fill-yellow-500' : ''}`} />
            </button>
            {userId && recipe.ownerId === userId && (
                <button 
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="p-2 bg-black/50 hover:bg-red-900/50 text-zinc-300 hover:text-red-400 rounded-full backdrop-blur-sm transition-colors"
                    title="Delete Recipe"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>

        {/* Preview Image */}
        {recipe.previewIcon ? (
            <div className="h-40 bg-zinc-950/50 flex items-center justify-center p-6 border-b border-zinc-800/50 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent opacity-50" />
                <img 
                    src={recipe.previewIcon} 
                    alt="" 
                    className="h-full w-full object-contain drop-shadow-xl group-hover:scale-110 transition-transform duration-500" 
                    style={{ imageRendering: 'pixelated' }}
                />
            </div>
        ) : (
            <div className="h-40 bg-zinc-950/50 flex items-center justify-center p-6 border-b border-zinc-800/50">
                <ChefHat className="w-12 h-12 text-zinc-800" />
            </div>
        )}
        
        <div className="p-5 flex-1 flex flex-col">
            <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-lg text-zinc-200 group-hover:text-yellow-500 transition-colors line-clamp-2 leading-tight">
                    {recipe.title}
                </h3>
            </div>
            
            {recipe.ownerId && (
                <div className="text-[10px] text-zinc-600 font-mono mb-2">
                    by {recipe.ownerName || "Anon"}
                </div>
            )}
            
            <div className="flex items-center gap-4 text-xs text-zinc-500 font-mono mt-auto pt-4 border-t border-zinc-800/50">
                <div className="flex items-center gap-1.5" title="Created At">
                    <Calendar className="w-3 h-3" />
                    <span suppressHydrationWarning>
                        {recipe.createdAt ? new Date(recipe.createdAt).toLocaleDateString('en-GB') : 'Unknown'}
                    </span>
                </div>
                <div className="flex items-center gap-1.5" title="Steps">
                    <GitGraph className="w-3 h-3" />
                    {recipe.nodeCount}
                </div>
                
                {/* Voting Mini-UI */}
                <div className="flex items-center gap-2 ml-auto">
                    <button 
                        onClick={(e) => handleVote(e, 'like')}
                        className="flex items-center gap-1 hover:text-green-400 transition-colors"
                    >
                        <ThumbsUp className="w-3 h-3" />
                        <span>{likes}</span>
                    </button>
                </div>
            </div>
        </div>
      </div>
    </Link>
  );
}